import { IngestionStatus } from "@/shared/domain/files";
import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "../approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  toolSuccess,
} from "../ClientTools";

import { OpenFileConfirmation } from "./confirmations";
import { markNavigatedToVisualizer } from "./workspaceActions";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface OpenFileInput {
  file_id: string;
  /** Timestamp in nanoseconds since Unix epoch. */
  timestamp?: number;
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: OpenFileInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.goto) {
    return toolError("Navigation is not available");
  }
  if (!deps.fileService) {
    return toolError("File service is not available");
  }

  if (!input.file_id) {
    return toolError("file_id is required");
  }

  try {
    const fileService = deps.fileService;
    const file = await fileService.getFileRecord(input.file_id);
    const timestamp =
      input.timestamp !== undefined ? BigInt(input.timestamp) : undefined;

    if (file.ingestion_status === IngestionStatus.NotIngested) {
      const filePageRoute = Goto.createFileRoute(file.file_id);
      deps.goto.navigateTo(filePageRoute);
      return toolSuccess("open_file", {
        navigated_to: filePageRoute,
        note: "File is not ingested; opened file detail page instead of visualizer.",
      });
    }

    deps.goto.visualize({
      dataSource: { files: [file.file_id] },
      params: { t: timestamp },
    });
    // This tool always opens a fresh visualizer, by design (see its
    // description). Recording that is what stops a batch-mate from opening a
    // second one on top of it.
    markNavigatedToVisualizer(deps);

    return toolSuccess("open_file", {
      navigated_to: Goto.route.Visualize,
    });
  } catch (err) {
    return toolError(err instanceof Error ? err.message : String(err));
  }
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<OpenFileInput>({
  name: "client_visualize_open_file",
  description: `Open a file in the visualizer (for ingested files with topics) or navigate to the file detail page (for non-ingested files). Always navigates to a fresh visualizer page, even if the user is already on the visualizer. Optionally seeks the timeline to a specific timestamp after opening.`,
  input_schema: {
    type: "object",
    properties: {
      file_id: {
        type: "string",
        description:
          "File ID (e.g., 'fl_abc123'). The file to open for visualization.",
      },
      timestamp: {
        type: "integer",
        description:
          "Timestamp in nanoseconds since Unix epoch. Optional — seeks the timer after opening.",
      },
    },
    required: ["file_id"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Open File",
  displayDescription: "Opens a file in the visualizer.",
  ConfirmationContent: OpenFileConfirmation,
});
